/**
 * Composant Calendar - Affichage interactif du calendrier
 * Utilise react-big-calendar avec personnalisation SING
 */

import { Calendar as BigCalendar, dateFnsLocalizer, View, Views } from 'react-big-calendar';
import { fr } from 'date-fns/locale';
import { format, parse, startOfWeek, getDay, startOfDay, isBefore } from 'date-fns';
import { useState, useCallback, useMemo } from 'react';
import { CalendarEventData } from '@/hooks/useCalendarEvents';
import '@/styles/calendar.css';

// Configuration locale française avec date-fns
const locales = {
  'fr': fr,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface CalendarProps {
  events: CalendarEventData[];
  onSelectEvent?: (event: CalendarEventData) => void;
  onSelectSlot?: (slotInfo: { start: Date; end: Date }) => void;
  defaultView?: View;
}

const messages = {
  allDay: 'Journée',
  previous: 'Précédent',
  next: 'Suivant',
  today: "Aujourd'hui",
  month: 'Mois',
  week: 'Semaine',
  day: 'Jour',
  agenda: 'Agenda',
  date: 'Date',
  time: 'Heure',
  event: 'Événement',
  noEventsInRange: 'Aucun événement dans cette période',
  showMore: (total: number) => `+ ${total} de plus`,
};

export function Calendar({
  events,
  onSelectEvent,
  onSelectSlot,
  defaultView = Views.MONTH,
}: CalendarProps) {
  const [view, setView] = useState<View>(defaultView);
  const [date, setDate] = useState(new Date());

  // Horaires d'ouverture
  const minTime = useMemo(() => {
    const d = new Date();
    d.setHours(8, 0, 0, 0);
    return d;
  }, []);

  const maxTime = useMemo(() => {
    const d = new Date();
    d.setHours(18, 0, 0, 0);
    return d;
  }, []);

  const today = useMemo(() => startOfDay(new Date()), []);

  // Griser dimanches (fermé), samedis (demi-journée), et jours passés (consultables)
  const dayPropGetter = useCallback((date: Date) => {
    const day = date.getDay();
    const isPast = isBefore(startOfDay(date), today);

    // Dimanche : fermé, non-cliquable
    if (day === 0) {
      return {
        className: isPast ? 'rbc-day-closed rbc-day-past' : 'rbc-day-closed',
        style: {
          backgroundColor: '#f0f0f0',
          pointerEvents: 'none' as const,
          opacity: 0.5,
        },
      };
    }
    // Jour passé : grisé mais cliquable (pour consulter les réservations)
    if (isPast) {
      return {
        className: 'rbc-day-past',
      };
    }
    // Samedi (demi-journée)
    if (day === 6) {
      return {
        className: 'rbc-day-half',
      };
    }
    return {};
  }, [today]);

  // Griser les créneaux hors horaires et passés
  const slotPropGetter = useCallback((date: Date) => {
    const day = date.getDay();
    const hour = date.getHours();
    const isPast = isBefore(date, new Date());

    // Dimanche : tout fermé
    if (day === 0) {
      return {
        className: 'rbc-slot-closed',
        style: { backgroundColor: '#f0f0f0', pointerEvents: 'none' as const },
      };
    }
    // Samedi après 13h : fermé
    if (day === 6 && hour >= 13) {
      return {
        className: 'rbc-slot-closed',
        style: { backgroundColor: '#f0f0f0', pointerEvents: 'none' as const },
      };
    }
    // Créneau passé : grisé mais visible
    if (isPast) {
      return {
        className: 'rbc-slot-past',
      };
    }
    return {};
  }, []);

  // Clic sur un slot : en vue Mois → naviguer vers vue Jour ; sinon comportement normal
  const handleSelectSlotFiltered = useCallback((slotInfo: { start: Date; end: Date; action: string }) => {
    const day = slotInfo.start.getDay();
    // Bloquer dimanche
    if (day === 0) return;
    // Bloquer samedi après 13h
    if (day === 6 && slotInfo.start.getHours() >= 13) return;

    // En vue Mois, clic sur la cellule → basculer en vue Jour
    if (view === Views.MONTH) {
      setDate(slotInfo.start);
      setView(Views.DAY);
      return;
    }

    if (onSelectSlot) {
      onSelectSlot({ start: slotInfo.start, end: slotInfo.end });
    }
  }, [onSelectSlot, view]);

  // Style personnalisé pour chaque événement
  const eventStyleGetter = useCallback((event: CalendarEventData) => {
    const style: React.CSSProperties = {
      backgroundColor: event.color || '#00758d',
      borderRadius: '6px',
      border: 'none',
      color: 'white',
      fontSize: '13px',
      fontWeight: 600,
      padding: '4px 8px',
    };

    return {
      style,
      className: `event-${event.type}`,
    };
  }, []);

  // Formater le titre des événements
  const EventComponent = useCallback(({ event }: { event: CalendarEventData }) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          background: event.accentColor || '#8E0B56',
          flexShrink: 0,
        }} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {event.title}
        </span>
      </div>
    );
  }, []);

  // Handler pour changement de vue
  const handleViewChange = useCallback((newView: View) => {
    setView(newView);
  }, []);

  // Handler pour navigation
  const handleNavigate = useCallback((newDate: Date) => {
    setDate(newDate);
  }, []);

  // Clic sur une date → naviguer en vue Jour
  const handleDrillDown = useCallback((drillDate: Date) => {
    setDate(drillDate);
    setView(Views.DAY);
  }, []);

  // Handler pour sélection d'événement
  const handleSelectEvent = useCallback((event: CalendarEventData) => {
    if (onSelectEvent) {
      onSelectEvent(event);
    }
  }, [onSelectEvent]);

  // Formats personnalisés
  const formats = useMemo(() => ({
    dayFormat: 'EEE dd',
    weekdayFormat: 'EEEE',
    monthHeaderFormat: 'MMMM yyyy',
    dayHeaderFormat: 'EEEE dd MMMM yyyy',
    dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${format(start, 'dd MMM', { locale: fr })} - ${format(end, 'dd MMM yyyy', { locale: fr })}`,
    agendaDateFormat: 'dd MMM',
    agendaTimeFormat: 'HH:mm',
    agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
      `${format(start, 'HH:mm', { locale: fr })} - ${format(end, 'HH:mm', { locale: fr })}`,
  }), []);

  return (
    <div style={{ height: '700px', background: 'white', borderRadius: '16px', padding: '0' }}>
      <BigCalendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100%' }}
        culture="fr"
        messages={messages}
        formats={formats}
        view={view}
        date={date}
        min={minTime}
        max={maxTime}
        onView={handleViewChange}
        onNavigate={handleNavigate}
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlotFiltered}
        onDrillDown={handleDrillDown}
        drilldownView={Views.DAY}
        selectable
        popup
        dayPropGetter={dayPropGetter}
        slotPropGetter={slotPropGetter}
        eventPropGetter={eventStyleGetter}
        components={{
          event: EventComponent,
        }}
      />
    </div>
  );
}
